/**
 * <hs-tab-row> — the product page's single information navigation.
 * A horizontally scrolling tablist where exactly one panel is open at a time.
 * Long panels collapse to a few lines behind a See more / Show less toggle.
 */
class HsTabRow extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.hs-tabs__list');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.prevArrow = this.querySelector('.hs-tabs__arrow--prev');
    this.nextArrow = this.querySelector('.hs-tabs__arrow--next');
    this.moreLabel = this.querySelector('[data-hs-more-label]')?.textContent.trim();
    this.lessLabel = this.querySelector('[data-hs-less-label]')?.textContent.trim();

    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
    });

    this.prevArrow?.addEventListener('click', () => this.scrollBy(-1));
    this.nextArrow?.addEventListener('click', () => this.scrollBy(1));
    this.list.addEventListener('scroll', () => this.updateArrows());
    new ResizeObserver(() => this.updateArrows()).observe(this.list);

    this.setupClamps();
    this.select(this.tabs[0], { scrollIntoView: false });
    this.updateArrows();
    // Clamp height is only measurable once the section stylesheet has applied.
    requestAnimationFrame(() => this.refreshClamps());

    this.addEventListener('hs:open-tab', (event) => {
      const target = this.tabs.find((tab) => tab.dataset.hsTabKey === event.detail?.key);
      if (target) this.select(target, { focus: true });
    });
  }

  select(tab, { scrollIntoView = true, focus = false } = {}) {
    // Cross-fade the panel switch — client request 2026-10-09 ("переключение
    // страниц... проверил?"): this used to be a flat `panel.hidden =
    // !isTarget` on every panel in one pass, an instant snap with zero
    // transition. Only the outgoing/incoming PAIR fades; every other panel
    // still gets the plain instant hidden toggle since it was never visible
    // to begin with. No prior selection (first connectedCallback call) has
    // nothing to fade FROM, so it falls through to the instant branch too —
    // matching every other first-paint state on this site.
    const outgoing = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    const outgoingPanel = outgoing && outgoing !== tab
      ? document.getElementById(outgoing.getAttribute('aria-controls'))
      : null;
    const incomingPanel = document.getElementById(tab.getAttribute('aria-controls'));

    this.tabs.forEach((other) => {
      const isTarget = other === tab;
      other.setAttribute('aria-selected', String(isTarget));
      other.setAttribute('tabindex', isTarget ? '0' : '-1');
      other.classList.toggle('hs-tabs__tab--active', isTarget);
      if (other !== outgoing && other !== tab) {
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      }
    });

    if (outgoingPanel && incomingPanel && outgoingPanel !== incomingPanel) {
      outgoingPanel.style.transition = 'opacity 0.15s ease';
      outgoingPanel.style.opacity = '0';
      setTimeout(() => {
        outgoingPanel.hidden = true;
        outgoingPanel.style.opacity = '';
        outgoingPanel.style.transition = '';
        incomingPanel.hidden = false;
        incomingPanel.style.opacity = '0';
        incomingPanel.style.transition = 'opacity 0.2s ease';
        void incomingPanel.offsetWidth;
        incomingPanel.style.opacity = '1';
        this.refreshClamps();
      }, 150);
    } else {
      if (incomingPanel) incomingPanel.hidden = false;
      this.refreshClamps();
    }

    if (scrollIntoView) tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    if (focus) tab.focus();
    this.updateArrows();
  }

  onKeydown(event, tab) {
    const index = this.tabs.indexOf(tab);
    let next = null;

    if (event.key === 'ArrowRight') next = this.tabs[index + 1];
    else if (event.key === 'ArrowLeft') next = this.tabs[index - 1];
    else if (event.key === 'Home') next = this.tabs[0];
    else if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
    else return;

    event.preventDefault();
    if (next) this.select(next, { focus: true });
  }

  scrollBy(direction) {
    this.list.scrollBy({ left: direction * this.list.clientWidth * 0.6, behavior: 'smooth' });
  }

  updateArrows() {
    if (!this.prevArrow || !this.nextArrow) return;
    const overflow = this.list.scrollWidth - this.list.clientWidth;
    if (overflow <= 1) {
      this.prevArrow.hidden = true;
      this.nextArrow.hidden = true;
      return;
    }
    this.prevArrow.hidden = false;
    this.nextArrow.hidden = false;
    this.prevArrow.disabled = this.list.scrollLeft <= 1;
    this.nextArrow.disabled = this.list.scrollLeft >= overflow - 1;
  }

  setupClamps() {
    this.clamps = Array.from(this.querySelectorAll('[data-hs-clamp]')).map((content) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hs-tabs__more hs-link';
      button.textContent = this.moreLabel;
      button.hidden = true;
      button.addEventListener('click', () => {
        const expanded = content.classList.toggle('hs-tabs__clamp--open');
        button.textContent = expanded ? this.lessLabel : this.moreLabel;
      });
      content.insertAdjacentElement('afterend', button);
      return { content, button };
    });
  }

  refreshClamps() {
    this.clamps?.forEach(({ content, button }) => {
      if (content.closest('[role="tabpanel"]')?.hidden) return;
      const overflows = content.scrollHeight - content.clientHeight > 2;
      button.hidden = !overflows && !content.classList.contains('hs-tabs__clamp--open');
    });
  }
}

if (!customElements.get('hs-tab-row')) {
  customElements.define('hs-tab-row', HsTabRow);
}

/* The delivery note under the size picker is a shortcut into the shipping tab. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-hs-open-shipping]');
  if (!trigger) return;

  const tabRow = trigger.closest('.product__info-container')?.querySelector('hs-tab-row');
  if (!tabRow) return;

  tabRow.dispatchEvent(new CustomEvent('hs:open-tab', { detail: { key: 'shipping' } }));
  tabRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
});
